import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { addData, getData } from "../api/data";
import {
  deleteUserOnCookie,
  getUserFromCookie,
  saveUserOnCookie,
} from "../cookies/cookies";
import { hideLoader, setError, setLoader } from "./quantomState";

const workersURI = "workers";

export const login = createAsyncThunk(
  `${workersURI}/admin-login`,
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoader());
      const userData = await addData(`${workersURI}/admin-login`, {
        email,
        password,
      });
      return userData;
    } catch (error) {
      dispatch(
        setError({
          error: error.response?.data?.error || "Error during login",
          errorTitle: "Login Error",
          timeout: false,
          warning: false,
        })
      );
      return rejectWithValue(error.response?.data || "Unknown error occurred");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const refreshToken = createAsyncThunk(
  `${workersURI}/refresh`,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      // With HTTP-only cookies, the refresh token is automatically sent
      // by the browser with the request - no need to manually include it
      const response = await addData(`${workersURI}/refresh`, {});

      return response;
    } catch (error) {
      // If refresh token fails, logout the user
      dispatch(logout());
      return rejectWithValue(error.response?.data || "Token refresh failed");
    }
  }
);

export const fetchUserInfo = createAsyncThunk(
  `${workersURI}/me`,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(setLoader());
      const userInfo = await getData(`${workersURI}/me`);
      return userInfo;
    } catch (error) {
      dispatch(
        setError({
          error: error.response?.data?.error || "Error fetching user info",
          errorTitle: "Fetch User Info Error",
          timeout: false,
          warning: false,
        })
      );
      return rejectWithValue(error.response?.data || "Unknown error occurred");
    } finally {
      dispatch(hideLoader());
    }
  }
);

export const logout = createAsyncThunk(
  `${workersURI}/logout`,
  async (_, { dispatch, rejectWithValue }) => {
    try {
      await addData(`${workersURI}/logout`);
    } catch (error) {
      // Even if logout API call fails, we still want to clean up local state
      // Don't show error for 401 since we're already handling that in the interceptor
      if (error.response?.status !== 401) {
        dispatch(
          setError({
            error: error.response?.data?.error || "Error during logout",
            errorTitle: "Logout Error",
            timeout: false,
            warning: false,
          })
        );
      }
      // Reject so the rejected case in reducer can reset state
      return rejectWithValue(error.response?.data || "Logout failed");
    } finally {
      // Always clean up local state, regardless of API call success/failure
      deleteUserOnCookie();
      dispatch(setLoader());
      dispatch(hideLoader());
    }
  }
);

const initialState = {
  user: null,
  token: "",
  isRefreshing: false,
};

const loginSlice = createSlice({
  name: "login",
  initialState,
  reducers: {
    loginFromCookie: (state, { payload }) => {
      return payload;
    },
    setRefreshing: (state, { payload }) => {
      state.isRefreshing = payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, { payload }) => {
        // Transform the response to match expected structure
        const transformedPayload = {
          user: payload.user,
          token: payload.accessToken, // Map accessToken to token
          isRefreshing: false,
        };
        saveUserOnCookie(transformedPayload);
        return transformedPayload;
      })
      .addCase(refreshToken.pending, (state) => {
        state.isRefreshing = true;
      })
      .addCase(refreshToken.fulfilled, (state, { payload }) => {
        // Transform the response to match expected structure
        const transformedPayload = {
          user: payload.user,
          token: payload.accessToken, // Map accessToken to token
          isRefreshing: false,
        };
        saveUserOnCookie(transformedPayload, true);
        return transformedPayload;
      })
      .addCase(refreshToken.rejected, (state) => {
        state.isRefreshing = false;
        return initialState;
      })
      .addCase(logout.fulfilled, () => {
        return initialState;
      })
      .addCase(logout.rejected, () => {
        // Even if logout API call fails, reset state
        return initialState;
      })
      .addCase(fetchUserInfo.fulfilled, (state, { payload }) => {
        const transformedPayload = {
          user: payload.user,
          token: state.token,
          isRefreshing: false,
        };
        saveUserOnCookie(transformedPayload, false);
        return transformedPayload;
      });
    // Add other cases as needed
  },
});

const { reducer, actions } = loginSlice;

export const { loginFromCookie, setRefreshing } = actions;

export default reducer;
